/* Scroll reveal.

   Wraps content that should arrive as the page reaches it rather than
   being fully formed before the reader gets there.

   Two rules shape the implementation:

   1. Nothing is hidden by the stylesheet alone. The hidden state is
      armed here, in JS, so a failed or blocked asset leaves the page
      readable instead of blank.

   2. Anything already on screen when this runs is left alone. Arming
      visible content would show it, hide it, then fade it back in —
      a flash. It also means the hero never needs a special case: it
      is on screen at upgrade time, so it opts itself out.

   3. A phone gets one arrival, early. A thumb-scroll covers a screen
      in a few hundred milliseconds, so a stagger that reads as a
      sequence on a trackpad reads on a phone as several things going
      off separately while the page is still moving — and a trigger
      tuned to fire as an element enters means the reader watches the
      last half of the transition from the middle of the screen. So
      below PHONE the stagger is dropped and the group is triggered
      slightly BEFORE it enters the viewport: content is finished by
      the time it is being read, rather than finishing under the eye.
*/

const REDUCED = '(prefers-reduced-motion: reduce)';
const PHONE = '(max-width: 749px)';

class AzReveal extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready !== undefined) return;
    this.dataset.ready = '';

    if (window.matchMedia(REDUCED).matches) return;
    if (!('IntersectionObserver' in window)) return;

    const selector = this.dataset.items;
    const items = selector
      ? [...this.querySelectorAll(selector)]
      : [...this.children];

    if (!items.length) return;

    // The wrapper is display:contents so it can never disturb a grid
    // or flex parent, which also means it has no box of its own —
    // the fold test and the observer both anchor to the first item.
    if (items[0].getBoundingClientRect().top < window.innerHeight * 0.9) return;

    const phone = window.matchMedia(PHONE).matches;

    // On a phone the whole group arrives at once. The delay custom
    // property is still written, because the armed/revealed CSS keys
    // off `[style*='--az-reveal-delay']` to reach items named by
    // data-items — it is simply always 0ms.
    const stagger = phone ? 0 : Number(this.dataset.stagger ?? 80);
    items.forEach((el, i) => {
      el.style.setProperty('--az-reveal-delay', `${i * stagger}ms`);
    });

    this.items = items;
    this.setAttribute('data-armed', '');

    this.io = new IntersectionObserver(this.onIntersect, {
      // Negative: wait until the element is 12% up the screen. Positive:
      // start it while it is still a tenth of a screen below the fold.
      rootMargin: phone ? '0px 0px 10% 0px' : '0px 0px -12% 0px',
    });
    this.io.observe(items[0]);
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  onIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    this.setAttribute('data-revealed', '');
    this.io.disconnect();

    // Once each item has landed, drop the inline delay and let the
    // filter go, so no element is left carrying a blur the compositor
    // has to keep honouring behind the 3D bottle further down.
    const last = this.items[this.items.length - 1];
    last.addEventListener(
      'transitionend',
      () => {
        this.removeAttribute('data-armed');
        this.items.forEach((el) => el.style.removeProperty('--az-reveal-delay'));
      },
      { once: true }
    );
  };
}

if (!customElements.get('az-reveal')) {
  customElements.define('az-reveal', AzReveal);
}
